import React, { useState } from "react";
import Layout from "../components/layout/GSTlayout";
import { useTheme } from '@mui/material/styles';

import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Container,
  Chip,
  Button,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Stack,
  Alert,
  LinearProgress,
  Fab,
} from "@mui/material";
import {
  AttachMoney as MoneyIcon,
  AssessmentOutlined as ReportIcon,
  AccountBalance as ReturnIcon,
  ReceiptLong as InvoiceIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  FileDownload as DownloadIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  Area,
  AreaChart,
} from "recharts";

// Enhanced Sample Data
const monthlyRevenueData = [
  { name: "Jan", Revenue: 400000, GST: 36000, Input: 15000, Net: 21000 },
  { name: "Feb", Revenue: 450000, GST: 40500, Input: 18000, Net: 22500 },
  { name: "Mar", Revenue: 420000, GST: 37800, Input: 16500, Net: 21300 },
  { name: "Apr", Revenue: 480000, GST: 43200, Input: 19200, Net: 24000 },
  { name: "May", Revenue: 500000, GST: 45000, Input: 20000, Net: 25000 },
  { name: "Jun", Revenue: 470000, GST: 42300, Input: 18500, Net: 23800 },
];

const gstComplianceData = [
  { name: "GSTR-1", value: 95, color: "#27AE60" },
  { name: "GSTR-3B", value: 90, color: "#F39C12" },
  { name: "Annual Return", value: 100, color: "#3498DB" },
];

const recentTransactions = [
  { id: "INV001", client: "ABC Corp", amount: 125000, gst: 11250, status: "Paid", date: "2024-06-10" },
  { id: "INV002", client: "XYZ Ltd", amount: 85000, gst: 7650, status: "Pending", date: "2024-06-09" },
  { id: "INV003", client: "PQR Industries", amount: 95000, gst: 8550, status: "Paid", date: "2024-06-08" },
  { id: "INV004", client: "LMN Services", amount: 75000, gst: 6750, status: "Overdue", date: "2024-06-05" },
];

const upcomingDeadlines = [
  { return: "GSTR-1", dueDate: "2024-07-11", status: "pending", days: 0 },
  { return: "GSTR-3B", dueDate: "2024-07-20", status: "upcoming", days: 9 },
  { return: "Annual Return", dueDate: "2024-12-31", status: "future", days: 203 },
];

const GstPortal = () => {
  const theme = useTheme();
  const [selectedPeriod, setSelectedPeriod] = useState("Monthly");
  const [chartView, setChartView] = useState("revenue");

  const MetricCard = ({ icon, title, value, subtitle, trend, trendValue, color }) => (
    <Card
      sx={{
        height: "100%",
        background: `linear-gradient(135deg, ${color}08 0%, ${color}04 100%)`,
        border: `1px solid ${color}20`,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: color,
        }
      }}
    >
      <CardContent sx={{ pb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Avatar sx={{ bgcolor: `${color}15`, color: color, width: 40, height: 40 }}>
                {icon}
              </Avatar>
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                {title}
              </Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700} color="text.primary" mb={0.5}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          {trend && (
            <Chip
              icon={trend === 'up' ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${trendValue}%`}
              size="small"
              color={trend === 'up' ? 'success' : 'error'}
              variant="outlined"
            />
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  const StatusChip = ({ status }) => {
    const getStatusConfig = (status) => {
      switch (status.toLowerCase()) {
        case 'paid':
          return { color: 'success', icon: <CheckCircleIcon /> };
        case 'pending':
          return { color: 'warning', icon: <ScheduleIcon /> };
        case 'overdue':
          return { color: 'error', icon: <WarningIcon /> };
        default:
          return { color: 'default', icon: null };
      }
    };

    const config = getStatusConfig(status);
    return (
      <Chip
        label={status}
        color={config.color}
        size="small"
        icon={config.icon}
        variant="filled"
      />
    );
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: `1px solid ${theme.palette.primary.main}20` }}>
          <Typography variant="subtitle2" mb={1}>{label}</Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="body2" sx={{ color: entry.color }}>
              {entry.name}: ₹{entry.value.toLocaleString()}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Layout>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Box mb={4}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h4" fontWeight={700} color="text.primary" gutterBottom>
                GST Portal Dashboard
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Monitor your GST compliance and financial metrics in real-time
              </Typography>
            </Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                sx={{ borderRadius: 2 }}
              >
                Export Report
              </Button>
              <Fab
                size="small"
                color="primary"
                aria-label="refresh"
                sx={{ boxShadow: theme.shadows[4] }}
              >
                <RefreshIcon />
              </Fab>
            </Stack>
          </Stack>

          {/* Alert for upcoming deadlines */}
          <Alert 
            severity="warning" 
            icon={<ScheduleIcon />}
            sx={{ mb: 3, borderRadius: 2 }}
          >
            <Typography variant="body2">
              <strong>Reminder:</strong> GSTR-1 filing due today. GSTR-3B due in 9 days.
            </Typography>
          </Alert>
        </Box>

        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<MoneyIcon />}
              title="Total Revenue"
              value="₹27.5L"
              subtitle="Last 6 months"
              trend="up"
              trendValue="12.5"
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<ReturnIcon />}
              title="GST Collected"
              value="₹2.48L"
              subtitle="Output GST"
              trend="up"
              trendValue="8.3"
              color={theme.palette.success.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<InvoiceIcon />}
              title="Active Invoices"
              value="1,245"
              subtitle="Current period"
              trend="up"
              trendValue="5.2"
              color={theme.palette.info.main}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard
              icon={<ReportIcon />}
              title="Compliance Score"
              value="95%"
              subtitle="All returns filed"
              trend="up"
              trendValue="2.1"
              color={theme.palette.warning.main}
            />
          </Grid>
        </Grid>

        {/* Charts Section */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Enhanced Revenue Chart */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={2} sx={{ p: 3, height: 450 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight={600}>
                  Revenue & GST Analytics
                </Typography>
                <ButtonGroup size="small" variant="outlined">
                  <Button
                    variant={chartView === 'revenue' ? 'contained' : 'outlined'}
                    onClick={() => setChartView('revenue')}
                  >
                    Revenue
                  </Button>
                  <Button
                    variant={chartView === 'gst' ? 'contained' : 'outlined'}
                    onClick={() => setChartView('gst')}
                  >
                    GST Analysis
                  </Button>
                </ButtonGroup>
              </Stack>
              
              <ResponsiveContainer width="100%" height={350}>
                {chartView === 'revenue' ? (
                  <AreaChart data={monthlyRevenueData}>
                    <defs>
                      <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.05}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
                    <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="Revenue"
                      stroke={theme.palette.primary.main}
                      strokeWidth={3}
                      fill="url(#colorRevenue)"
                    />
                  </AreaChart>
                ) : (
                  <BarChart data={monthlyRevenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.grey[200]} />
                    <XAxis dataKey="name" stroke={theme.palette.text.secondary} />
                    <YAxis stroke={theme.palette.text.secondary} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="GST" fill={theme.palette.success.main} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Input" fill={theme.palette.warning.main} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="Net" fill={theme.palette.info.main} radius={[4, 4, 0, 0]} />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Enhanced Compliance Chart */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={2} sx={{ p: 3, height: 450 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                GST Compliance Status
              </Typography>
              
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={gstComplianceData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {gstComplianceData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>

              <Stack spacing={2} mt={2}>
                {gstComplianceData.map((item, index) => (
                  <Box key={index}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="body2" fontWeight={500}>
                        {item.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {item.value}%
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={item.value}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: `${item.color}20`,
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: item.color,
                          borderRadius: 3,
                        }
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            </Paper>
          </Grid>
        </Grid>

        {/* Data Tables Section */}
        <Grid container spacing={3}>
          {/* Recent Transactions */}
          <Grid item xs={12} lg={8}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Recent Transactions
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice ID</TableCell>
                      <TableCell>Client</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="right">GST</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentTransactions.map((transaction) => (
                      <TableRow
                        key={transaction.id}
                        sx={{ '&:hover': { backgroundColor: theme.palette.grey[50] } }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {transaction.id}
                          </Typography>
                        </TableCell>
                        <TableCell>{transaction.client}</TableCell>
                        <TableCell align="right">
                          ₹{transaction.amount.toLocaleString()}
                        </TableCell>
                        <TableCell align="right">
                          ₹{transaction.gst.toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={transaction.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {new Date(transaction.date).toLocaleDateString('en-IN')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Upcoming Deadlines */}
          <Grid item xs={12} lg={4}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} mb={3}>
                Upcoming Deadlines
              </Typography>
              <Stack spacing={3}>
                {upcomingDeadlines.map((deadline, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 2,
                      border: `1px solid ${theme.palette.grey[200]}`,
                      borderRadius: 2,
                      backgroundColor: deadline.days === 0 ? `${theme.palette.error.main}08` : 'transparent'
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {deadline.return}
                      </Typography>
                      <Chip
                        label={deadline.days === 0 ? 'Due Today' : `${deadline.days} days`}
                        size="small"
                        color={deadline.days === 0 ? 'error' : deadline.days <= 7 ? 'warning' : 'default'}
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      Due: {new Date(deadline.dueDate).toLocaleDateString('en-IN')}
                    </Typography>
                  </Box>
                ))}
              </Stack>
              
              <Button
                variant="contained"
                fullWidth
                sx={{ mt: 3, borderRadius: 2 }}
                startIcon={<ReportIcon />}
              >
                File Returns
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default GstPortal;
